import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import _throttle from 'lodash/throttle';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import memoize from 'memoize-one';
import ReactTooltip from 'react-tooltip';
import {
  sendAnalyticsEvent,
  updateSRLWidgetsHeight,
} from '../../../actions/reports';
import {
  toggleInsightDetailsModal,
  getDashboardsWidgets,
  getSRLSurveys,
  getSRLSurveyResults,
  addWidgetToDashboard,
  updateWidgets,
  containerWidthChange,
} from '../../../actions/insights';
import {
  WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART,
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_LOGIN_PIE_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
} from '../../constants';
import CustomMuiDropdown from '../../../../components/CustomMuiDropdown';
import MLButton from '../../../../components/MLButton';
import MLIcon from '../../../../components/MLIcon';
import MLDialog from './components/MLDialog';
import MLAlert from '../../../../components/MLAlert';
import WidgetLibrary from './components/WidgetLibrary';
import WidgetWrapper from './components/WidgetWrapper';
import WidgetEmptyState from './components/WidgetEmptyState';
import WidgetContainer from './components/WidgetContainer';
import Loader from '../Loader';
import BetaBadge from './components/BetaBadge/BetaBadge';
import EmptySRLDashboard from './components/EmptySRLDashboard/EmptySRLDashboard';

import { VIEW_QQ_INSIGHTS_REPORT, selectAssignment } from '../../../analyticsEvents';

import GridLayout from './components/GridLayout/GridLayout';
import style from './QuantQualInsights.module.css';
import { getConfigObject } from '../../../util/config';


const MOBILE_XS = 425;
const WIDGET_HEIGHT = 426;
const WIDGET_HEIGHT_TALLER = 650;

class QuantQualInsights extends Component {
  static propTypes = {
    match: PropTypes.object,
    loading: PropTypes.bool,
    dashboard: PropTypes.object,
    reports: PropTypes.object,
    surveys: PropTypes.object,
    onTabChange: PropTypes.func,
    widgetsV2: PropTypes.array,
    liftAssignments: PropTypes.func,
    getSRLDropdown: PropTypes.func,
    refreshReport: PropTypes.func,
    sendAnalyticsEvent: PropTypes.func,
    addWidgetToDashboard: PropTypes.func,
    updateWidgets: PropTypes.func,
    courseAbstract: PropTypes.object,
    isInsightLODetailModalVisible: PropTypes.bool,
    isInsightAssignmentDetailModalVisible: PropTypes.bool,
    isInsightExternalLOModalVisible: PropTypes.bool,
    toggleInsightDetailsModal: PropTypes.func.isRequired,
    getDashboardsWidgets: PropTypes.func.isRequired,
    getSRLSurveys: PropTypes.func.isRequired,
    getSRLSurveyResults: PropTypes.func.isRequired,
    widgetDetailModalVisible: PropTypes.number,
    assignments: PropTypes.array,
    containerWidthChange: PropTypes.func,
    millieBot: PropTypes.shape({
      isLoaded: PropTypes.bool,
      start: PropTypes.func,
      stop: PropTypes.func,
      remove: PropTypes.func,
    }),
  }

  FF_REPORTS_V2 = getConfigObject().FF_REPORTS_V2 ?? false;

  constructor(props) {
    super(props);
    const currentSurveyList = get(props.surveys, 'srlSurveyList', []);
    this.state = {
      isWidgetLibraryVisible: false,
      isWidgetSuccess: false,
      isWidgetError: false,
      widgetErrorMessage: '',
      widgetSuccessMessage: 'Success!',
      srlDropdownAnchor: false,
      srlSelectedOption: currentSurveyList.length > 0 ? {
        label: currentSurveyList[0].name,
        value: currentSurveyList[0].id,
      } : {},
      clientWidth: 0,
    };
  }

  updateClientWidth = _throttle(() => {
    const clientWidth = window.innerWidth;
    const newHeight = clientWidth <= MOBILE_XS ? WIDGET_HEIGHT_TALLER : WIDGET_HEIGHT;
    if (this.props.SRLWidgetsHeight !== newHeight) {
      this.props.updateSRLWidgetsHeight(newHeight);
    }
    this.setState({ clientWidth });
  }, 300);

  componentDidMount() {
    const { courseAbstract } = this.props;
    const courseId = courseAbstract.id;
    this.props.getDashboardsWidgets(courseId);
    this.props.getSRLSurveys(courseId);
    this.props.sendAnalyticsEvent(VIEW_QQ_INSIGHTS_REPORT);
    this.updateClientWidth();
    window.addEventListener('resize', this.updateClientWidth);
  }

  componentDidUpdate(prevProps) {
    const prevSurveyList = get(prevProps.surveys, 'srlSurveyList', []);
    const currentSurveyList = get(this.props.surveys, 'srlSurveyList', []);

    if (this.props.liftAssignments) {
      this.props.liftAssignments(this.props.assignments);
    }

    if (currentSurveyList.length > 0 && prevSurveyList.length !== currentSurveyList.length) {
      const survey = currentSurveyList[0];
      this.setState({
        srlSelectedOption: {
          label: survey.name,
          value: survey.id,
        },
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateClientWidth);
  }

  getInsightAssignmentSubtitle() {
    const tenDaysInThePast = moment().subtract(10, 'days').format('dddd, MMMM D');
    const fourDaysInTheFuture = moment().add(4, 'days').format('dddd, MMMM D');
    return `${tenDaysInThePast} - ${fourDaysInTheFuture}`;
  }

  getWidgetComponents = memoize(dWidgets => dWidgets
    .filter(dw => {
        const {
          FEATURE_USE_EXTERNAL_INSIGHTS,
          FEATURE_USE_SCORE_HISTOGRAM_CHART,
          FEATURE_USE_LOGIN_INSIGHTS,
          FEATURE_USE_TIME_SCORE_CHART,
          FEATURE_USE_TIME_ATTEMPTS_CHART,
          FEATURE_USE_COMPLETED_ASSIGNMENT,
        } = getConfigObject();

        return (
          (FEATURE_USE_EXTERNAL_INSIGHTS || dw.widget_type !== WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART) &&
          (FEATURE_USE_SCORE_HISTOGRAM_CHART || dw.widgetType !== WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART) &&
          (FEATURE_USE_TIME_SCORE_CHART || dw.widgetType !== WIDGET_TYPE_TIME_SCORE_CHART) &&
          (FEATURE_USE_TIME_ATTEMPTS_CHART || dw.widgetType !== WIDGET_TYPE_TIME_ATTEMPTS_CHART) &&
          (FEATURE_USE_COMPLETED_ASSIGNMENT  || dw.widgetType !== WIDGET_TYPE_COMPLETED_ASSIGNMENT) &&
          (FEATURE_USE_LOGIN_INSIGHTS || dw.widgetType !== WIDGET_TYPE_LOGIN_PIE_CHART));
    })
    .map(dw => ({ ...dw, widget: this.widgetComponent(dw) })))

  handleWidgetError(widgetErrorMessage) {
    this.setState({ isWidgetError: true, widgetErrorMessage });
    setTimeout(() => this.setState({ isWidgetError: false, widgetErrorMessage: '' }), 3000);
  }

  handleWidgetSuccess = (widgetSuccessMessage) => {
    this.setState({ widgetSuccessMessage, isWidgetSuccess: true });
    setTimeout(() => this.setState({ isWidgetSuccess: false, widgetSuccessMessage: '' }), 3000);
  }

  // Todo: include a validation in the backend or a flag in the database to identify this
  isASRLEnglishVersion = (srlSelectedOption) => srlSelectedOption.label.toLowerCase().includes('english');

  widgetComponent = (dw) => {
    const currentSurveyList = get(this.props.surveys, 'srlSurveyList', []);
    let currentSurvey = {};
    let isEnglishVersion = false;
    if (currentSurveyList.length > 0) {
      currentSurvey = currentSurveyList.find(survey => survey.id === this.state.srlSelectedOption.value) || {};
      isEnglishVersion = this.isASRLEnglishVersion(this.state.srlSelectedOption);
    }
    return (
      <WidgetWrapper
        limit={3}
        widget={{ widget: dw }}
        courseId={this.props.courseAbstract.id}
        courseShortId={this.props.courseAbstract.shortId}
        productModelId={this.props.courseAbstract.productModelId}
        activityId={currentSurvey.activity_id}
        tool={currentSurvey.tool}
        isDetailView={false}
        hasStudents={this.props.courseAbstract.enrollments > 0}
        onTabChange={this.props.onTabChange}
        toggleInsightDetailsModal={this.props.toggleInsightDetailsModal}
        sendAnalyticsEvent={this.props.sendAnalyticsEvent}
        handleWidgetSuccess={this.handleWidgetSuccess}
        handleWidgetError={this.handleWidgetError}
        isSRLIntroSurvey={currentSurvey.is_intro_survey}
        isSRLEnglishVersion={isEnglishVersion}
        isSRLFirstCheckpoint={currentSurvey.is_first_checkpoint}
        reportsV2={this.FF_REPORTS_V2}
      />
    );
  }

  toggleWidgetLibrary = (event) => {
    event.stopPropagation();
    this.setState({ isWidgetLibraryVisible: !this.state.isWidgetLibraryVisible });
  }

  handleAddWidget = async (widgetId) => {
    try {
      const { dashboard, courseAbstract } = this.props;

      const widgets = get(dashboard, 'widgets', []);
      const courseId = courseAbstract.id;
      const dashboardId = dashboard && dashboard.dashboardId;

      const currentWidgetsAmout = widgets.length;
      const x = currentWidgetsAmout % 2;
      const y = Math.floor(currentWidgetsAmout / 2);
      const response = await this.props.addWidgetToDashboard(courseId, dashboardId, widgetId, { x, y, w: 1, h: 1 });
      if (response && !response.error) {
        this.handleWidgetSuccess('Widget added');
      } else {
        throw new Error();
      }
    } catch (_error) {
      this.handleWidgetError('There was an issue adding the widget. Please try again.');
    }
  }

  handleSRLDropdownOpen = (event) => {
    event.stopPropagation();
    this.setState({ srlDropdownAnchor: event.currentTarget });
  }

  handleSRLDropdownClick = (item, event) => {
    event.stopPropagation();
    const surveyResults = get(this.props.surveys, 'srlSurveyResults', {});
    // Only fetch survey data if we haven't already gotten it
    if (!surveyResults[item.value]) {
      this.props.getSRLSurveyResults(this.props.courseAbstract.id, item.value);
    }
    this.setState({ srlSelectedOption: item, srlDropdownAnchor: null });
    this.props.sendAnalyticsEvent(selectAssignment(item));
  }

  handleSRLDropdownClose = () => {
    this.setState({ srlDropdownAnchor: null });
  };

  renderInsightsDashboard = (isLegacy) => {
    const {
      dashboard,
      onTabChange,
      widgetsV2,
      courseAbstract,
      widgetDetailModalVisible,
    } = this.props;
    const widgets = this.FF_REPORTS_V2 ? widgetsV2 : get(dashboard, 'widgets', []);

    const hasWidgets = Boolean(widgets.length);
    const courseId = courseAbstract.id;
    const { enrollments } = courseAbstract;
    const hasStudents = enrollments > 0;
    const widgetsComponents = this.getWidgetComponents(widgets);
    const widgetDataForDetailModal = widgets.find(w => w.dashboardWidgetId === widgetDetailModalVisible);

    return (
      <Fragment>
        {!this.FF_REPORTS_V2 && isLegacy ? (
          <div className={style.QuantQualInsightsHeaderGrid}>
            {this.renderAddInsightButton()}
          </div>
        ) : null}
         {hasWidgets ? (
           <GridLayout
             widgets={widgetsComponents}
             onWidthChange={this.props.containerWidthChange}
             hidePadding={getConfigObject().FEATURE_USE_SRL_LAYOUT}
             margin={this.FF_REPORTS_V2 ? [24, 24] : [10, 10]}
           />
         ) : (
          <WidgetEmptyState />
        )}
        <ReactTooltip className={style.toolTipText} effect="solid" multiline />

        {widgetDetailModalVisible !== 0 && widgetDataForDetailModal && (
          <WidgetWrapper
            isDetailView
            isInsightDetailModalVisible
            key={`detail_${courseId}_${widgetDataForDetailModal.dashboardWidgetId}`}
            widget={{ widget: widgetDataForDetailModal }}
            courseId={courseId}
            hasStudents={hasStudents}
            onTabChange={onTabChange}
            toggleInsightDetailsModal={this.props.toggleInsightDetailsModal}
            sendAnalyticsEvent={this.props.sendAnalyticsEvent}
            reportsV2={this.FF_REPORTS_V2}
          />
        )}
      </Fragment>
    );
  }

  renderSRLDropdown = () => {
    const srlSurveyList = get(this.props.surveys, 'srlSurveyList', []);
    const srlDropdownOptions = srlSurveyList.map(survey => ({ label: survey.name, value: survey.id }));

    if (srlDropdownOptions.length > 0) {
      return (
        <div className={style.srlDropdownContainer}>
          <span> Showing Results for: </span>
          <CustomMuiDropdown
            className={style.srlDropdown}
            labelClassName={style.srlDropdownLabel}
            optionsClassName={style.srlDropdownOptions}
            items={srlDropdownOptions}
            selectedValue={this.state.srlSelectedOption.value}
            onClick={this.handleSRLDropdownOpen}
            onClickItem={this.handleSRLDropdownClick}
            onClose={this.handleSRLDropdownClose}
          />
        </div>
      );
    }
    return null;
  };

  renderAddInsightButton = () => (
    <MLButton
      data-id="__show_widget_library"
      testId="show_widget_library_btn"
      type="submit"
      btnType="primary"
      onClick={this.toggleWidgetLibrary}
    >
      <div>
        <MLIcon
          className={style.btnIcon}
          type="plus"
          role="presentation"
        />
        <span>Add Insight</span>
      </div>
    </MLButton>
  );

  renderSRLDashboard = () => {
    if (!this.state.srlSelectedOption.value) {
      return (
        <EmptySRLDashboard />
      );
    } else if (this.state.srlSelectedOption.value) {
      const selectedResults = get(this.props.surveys, `srlSurveyResults[${this.state.srlSelectedOption.value}]`, []);
      const srlWidgetsComponents = this.getWidgetComponents(selectedResults);
        return (
          <GridLayout
            widgets={srlWidgetsComponents}
            hidePadding
            onWidthChange={this.props.containerWidthChange}
            enableResponsiveHeight
            widgetHeight={this.props.SRLWidgetsHeight}
            margin={this.FF_REPORTS_V2 ? [24, 24] : [10, 10]}
          />
        );
    }
    return null;
  }

  renderDashboard = () => {
    const performanceLabel = `Student Performance and Activity (${this.props.dashboard.widgets.length})`;
    const goalSettingLabel = 'Goal-setting and Reflection Responses';

    if  (this.FF_REPORTS_V2) {
      if (this.props.widgetsV2) {
        return (
          <>
            {this.renderInsightsDashboard(false)}
          </>
        );
      } else {
        this.props.getSRLDropdown(this.renderSRLDropdown());
        this.props.setSelectedSurveyAssignmentId(this.state.srlSelectedOption.value);
        return (
          <>
            {this.renderSRLDashboard()}
          </>
        );
      }
    }

    return (
      <div className={!this.FF_REPORTS_V2 && style.accordionContainer}>
        <WidgetContainer
          label="goal-setting"
          clickOverride={this.state.srlDropdownAnchor ? this.handleSRLDropdownClose : null}
          title={
            <div className={`${style.widgetContainerHeader} ${style.responsive}`}>
              <span className={style.widgetContainerTitle}>
                {goalSettingLabel}
                <BetaBadge className={style.betaBadge} />
              </span>
              {this.renderSRLDropdown()}
            </div>
          }
          minWidthTitle={320}
          enableAutoHeight
        >
          {this.renderSRLDashboard()}
        </WidgetContainer>
        <WidgetContainer
          label="student-performance"
          title={
            <div className={style.widgetContainerHeader}>
              <span className={style.widgetContainerTitle}>{performanceLabel}</span>
              {this.renderAddInsightButton()}
            </div>
          }
          minWidthTitle={320}
        >
          {this.renderInsightsDashboard(false)}
        </WidgetContainer>
      </div>
    );
  }

  render() {
    const { loading, courseAbstract, dashboard } = this.props;

    if (loading) return <Loader />;

    const dashboardId = dashboard && dashboard.dashboardId;
    const isWidgetLibraryVisisble = Boolean(dashboardId) && this.state.isWidgetLibraryVisible;
    const courseId = courseAbstract.id;

    return (
      <div>
        <MLDialog
          title="Add an Insight"
          isOpen={isWidgetLibraryVisisble}
          handleClose={this.toggleWidgetLibrary}
        >
          <WidgetLibrary courseId={courseId} handleAddWidget={this.handleAddWidget} handleWidgetError={this.handleWidgetError} />
        </MLDialog>
        <MLAlert
          dismissible
          top={10}
          show={this.state.isWidgetSuccess}
          onDismiss={() => {
            this.setState({ isWidgetSuccess: false });
          }}
          text={this.state.widgetSuccessMessage}
          alertType="success"
          className={style.alert}
        />
        <MLAlert
          dismissible
          top={10}
          show={this.state.isWidgetError}
          onDismiss={() => this.setState({ isWidgetError: false, widgetErrorMessage: '' })}
          text={this.state.widgetErrorMessage}
          alertType="error"
          className={style.alert}
        />
        {getConfigObject().FEATURE_USE_SRL_LAYOUT ? this.renderDashboard() : this.renderInsightsDashboard(true)}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const loading = get(state, 'insights.isDashboardLoading');
  const widgetDetailModalVisible = get(state, 'insights.widgetDetailModalVisible', false);
  const SRLWidgetsHeight =  get(state, 'insights.SRLWidgetsHeight');
  const assignments = get(state, 'insights.dashboard.assignments', []);

  return {
    loading,
    widgetDetailModalVisible,
    reports: state.reports,
    dashboard: state.insights.dashboard,
    surveys: state.insights.surveys,
    SRLWidgetsHeight,
    assignments,
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({
  toggleInsightDetailsModal,
  sendAnalyticsEvent,
  getDashboardsWidgets,
  getSRLSurveys,
  getSRLSurveyResults,
  addWidgetToDashboard,
  containerWidthChange,
  updateWidgets,
  updateSRLWidgetsHeight,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QuantQualInsights);
